import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/scrim?tab=designer).
 *
 * @slot default - Slot para o conteúdo principal a ser exibido sobre o fundo escurecido do scrim.
 * @slot activator - Slot para o elemento ativador do scrim, com prioridade sobre a propriedade activator.
 */
@Component({
  tag: 'br-scrim',
  styleUrl: 'scrim.scss',
  shadow: true,
  formAssociated: true,
})
export class Scrim {
  /**
   * Referência ao elemento host do componente.
   */
  @Element() el: HTMLBrScrimElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Ativa/desativa o scrim
   */
  @Prop({ reflect: true, mutable: true }) isOpen: boolean = false

  /**
   * Posiciona o conteúdo no topo, centro, direita, esquerda, abaixo dentro do scrim
   */
  @Prop() positionContent: 'top' | 'center' | 'right' | 'left' | 'bottom'

  /**
   * Desativa o fechamento do scrim ao ser clicado
   */
  @Prop() disableCloseOnClick: boolean = false

  /**
   * Define o valor de z-index do scrim
   */
  @Prop() zIndex: number | null = null

  /**
   * Cor de fundo personalizada para o scrim.
   * Aceita os seguintes formatos de cor:
   * - Cores nomeadas do CSS: 'red', 'blue', 'green', 'yellow', etc.
   * - Códigos hexadecimais: '#ff0000', '#00ff00', '#0000ff', etc.
   * - Valores RGB: 'rgb(255, 0, 0)', 'rgb(0, 255, 0)', etc.
   * - Valores RGBA: 'rgba(255, 0, 0, 0.5)', 'rgba(0, 255, 0, 0.8)', etc.
   * - Valores HSL: 'hsl(0, 100%, 50%)', 'hsl(120, 100%, 50%)', etc.
   * - Valores HSLA: 'hsla(0, 100%, 50%, 0.5)', 'hsla(240, 100%, 50%, 0.7)', etc.
   * Se não especificada, usa a cor padrão do tema.
   */
  @Prop() bgColor: string | null = null

  /**
   * Define a opacidade personalizada do scrim
   */
  @Prop() customOpacity: number | null = null

  /**
   * Define o modo de exibição do scrim:
   * - `'fullscreen'`: Ocupa toda a tela (`position: fixed`). (_padrão_)
   * - `'parent'`: Ocupa apenas o elemento pai (`position: absolute`).
   *   O elemento pai deve ter `position: relative` ou outro valor diferente de `static`.
   *
   * Para a variante `'legibility'`, este atributo é ignorado: o posicionamento é sempre
   * calculado automaticamente a partir das coordenadas do elemento pai.
   */
  @Prop({ reflect: true }) displayMode: 'fullscreen' | 'parent' = 'fullscreen'

  /**
   * Define a estratégia de manipulação de rolagem quando scrim está aberto
   * - 'block': Impede a rolagem completamente
   * - 'close': Fecha o scrim quando ocorre rolagem
   */
  @Prop() scrollStrategy: 'block' | 'close'

  /**
   * Determina quanto de rolagem (em pixels) é necessário para acionar a ação de fechamento automático do scrim.
   */
  @Prop({ mutable: true }) scrollThreshold: number = 50

  /**
   * Define o seletor para o elemento activator.
   * Nota: O slot 'activator' tem prioridade sobre esta propriedade.
   */
  @Prop() activator: string | null = null

  /**
   * Define um rótulo acessível personalizado para o diálogo.
   * Se não fornecido, será usado "Conteúdo do diálogo" como padrão.
   */
  @Prop() ariaLabel: string | null = null

  /**
   * Define a variante semântica do scrim
   * - `'focus'`: Redireciona o foco hierárquico do usuário. Cor #000000 com opacidade 40%. (_padrão_)
   * - `'spotlight'`: Scrim vazado — destaca um elemento específico criando uma fresta no overlay.
   *   Aplica as mesmas cores da variante `'focus'`. Use `spotlightTargetId` para indicar o elemento a ser destacado.
   * - `'legibility'`: Melhora o contraste e leitura de texto sobre superfícies. Cor #000000 com opacidade 64%.
   *   Para cobertura parcial, use `legibilityAnchor` + `legibilitySize`.
   *   Para gradiente suave, use `bgColor` com um valor de gradiente CSS e `customOpacity="1"`,
   *   ex.: `bg-color="linear-gradient(to top, rgba(0,0,0,0.64), transparent)"`..
   *
   * Quando definida, aplica automaticamente as especificações de cor e opacidade do Design System.
   * As propriedades `bgColor` e `customOpacity` têm prioridade e sobrepõem os valores da variante.
   */
  @Prop() variant: 'focus' | 'spotlight' | 'legibility' = 'focus'

  /**
   * Define a borda de ancoragem da faixa de cobertura da variante `legibility`.
   *
   * Controla de qual borda (ou centro) do elemento a máscara de overlay cresce,
   * tendo seu tamanho determinado por `legibilitySize`.
   *
   * - `'top'`: faixa ancorada na borda superior, cresce para baixo.
   * - `'bottom'`: faixa ancorada na borda inferior, cresce para cima.
   * - `'left'`: faixa ancorada na borda esquerda, cresce para a direita.
   * - `'right'`: faixa ancorada na borda direita, cresce para a esquerda.
   * - `'center'`: faixa centralizada verticalmente no elemento.
   *
   * Quando `legibilitySize` é `null`, a máscara ocupa 100% independentemente
   * da âncora definida, equivalendo a uma cobertura total.
   *
   * Só tem efeito quando `variant="legibility"`.
   */
  @Prop() legibilityAnchor: 'top' | 'bottom' | 'left' | 'right' | 'center' = 'bottom'

  /**
   * Define o tamanho da faixa de cobertura da variante `legibility`, usado em
   * conjunto com `legibilityAnchor`.
   *
   * Aceita qualquer valor CSS de comprimento válido:
   * - Percentual relativo ao elemento pai: `'40%'`, `'75%'`
   * - Comprimento absoluto: `'120px'`, `'8rem'`, `'6em'`
   * - Função CSS: `'calc(100% - 2rem)'`
   *
   * Quando `null` (padrão), a máscara ocupa **100%** da dimensão relevante:
   * - **altura** para âncoras `top`, `bottom` e `center`
   * - **largura** para âncoras `left` e `right`
   *
   * Só tem efeito quando `variant="legibility"` está definido.
   */
  @Prop() legibilitySize: string | null = null

  /**
   * Ativa o modo de scrim vazado (variante 'spotlight'), criando uma área de fresta no overlay
   * que destaca o elemento referenciado pelo seletor CSS fornecido.
   */
  @Prop() spotlightTargetId: string | null = null

  /**
   * Espaçamento interno (em pixels) ao redor da área de fresta no scrim vazado.
   */
  @Prop() spotlightPadding: number = 8

  /**
   * Define a forma da área de fresta no scrim vazado.
   * - 'rect': Retangular com bordas retas.
   * - 'rounded': Retangular com bordas arredondadas (border-radius de 8px).
   * - 'circle': Elipse inscrita na área do elemento alvo.
   */
  @Prop() spotlightShape: 'rect' | 'rounded' | 'circle' = 'rect'

  /** Indica que o scrim foi aberto. */
  @Event({ bubbles: true, composed: true }) brScrimOpen: EventEmitter<void>

  /** Indica que o scrim foi fechado */
  @Event({ bubbles: true, composed: true }) brScrimClose: EventEmitter<void>

  @State() isActive: boolean = false
  @State() hasActivatorSlot: boolean = false
  /** Rect do elemento alvo do scrim vazado. */
  @State() spotlightRect: DOMRect | null = null

  /** Indica se o atributo `display-mode` foi explicitamente informado pelo usuário */
  private displayModeExplicit: boolean = false
  // ======================================================================
  // PROPRIEDADES PRIVADAS E VARIÁVEIS INTERNAS
  // ======================================================================
  private contentRef: HTMLDivElement | null = null
  private activatorElement: HTMLElement | null = null
  private scrollOffset: number = 0
  private lastScrollTop: number = 0
  private scrollOrigin: number | null = null
  private previouslyFocusedElement: HTMLElement | null = null
  private focusTimeoutId: ReturnType<typeof setTimeout> | null = null
  private restoreFocusTimeoutId: ReturnType<typeof setTimeout> | null = null
  /** ResizeObserver que monitora o elemento alvo do spotlight para recalcular a posição. */
  private spotlightResizeObserver: ResizeObserver | null = null
  /** Elemento atualmente sendo destacado pelo scrim vazado. */
  private spotlightTargetElement: Element | null = null
  /** Referência ao div interno do scrim para aplicação de estilos de posicionamento da variante legibility. */
  private scrimDivRef: HTMLDivElement | null = null
  /** ResizeObserver que monitora o elemento pai para recalcular a posição da variante legibility. */
  private legibilityResizeObserver: ResizeObserver | null = null

  /**
   * Flag que suprime handleGlobalFocus enquanto a modal filha está
   * gerenciando ativamente sua própria sequência de foco (moveFocusIntoModal).
   */
  private isModalFocusing: boolean = false
  private modalFocusSuppressId: NodeJS.Timeout | null = null

  private static readonly FOCUSABLE_SELECTORS = [
    'a[href]:not([disabled])',
    'button:not([disabled])',
    'br-button:not([disabled])',
    'input:not([disabled])',
    'br-input:not([disabled])',
    'select:not([disabled])',
    'br-select:not([disabled])',
    'textarea:not([disabled])',
    'br-textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',')

  // ======================================================================
  // LIFECYCLE HOOKS E WATCHERS
  // ======================================================================

  @Watch('isOpen')
  handleOpenChange(newValue: boolean) {
    // Cancelar qualquer timeout anterior
    if (this.focusTimeoutId !== null) {
      clearTimeout(this.focusTimeoutId)
      this.focusTimeoutId = null
    }
    if (this.restoreFocusTimeoutId !== null) {
      clearTimeout(this.restoreFocusTimeoutId)
      this.restoreFocusTimeoutId = null
    }

    // Legibility é sempre ativo — ignora tentativas de fechar
    if (this.isLegibilityVariant) {
      this.isActive = true
      this.updateStyles()
      if (newValue) this.brScrimOpen.emit()
      return
    }

    this.isActive = newValue
    if (newValue && this.scrollStrategy === 'close') {
      const currentScroll = window.scrollY
      this.lastScrollTop = currentScroll
      this.scrollOrigin = currentScroll
    }
    this.updateStyles()

    if (this.isActive) {
      this.previouslyFocusedElement = document.activeElement as HTMLElement
      this.applyScrollStrategy()
      this.addGlobalEventInterception()

      const hasModalChild = this.findModalChild() !== null
      if (hasModalChild) {
        this.suppressFocusGuard()
      } else {
        this.focusTimeoutId = setTimeout(() => {
          this.focusFirstElement()
        }, 100)
      }
      this.setupSpotlight()
      this.brScrimOpen.emit()
    } else {
      this.closeModalChild()
      this.cleanupSpotlight()
      this.resetScrolling()
      this.removeGlobalEventInterception()
      this.restoreFocus()

      this.brScrimClose.emit()
    }
  }

  @Watch('zIndex')
  @Watch('bgColor')
  @Watch('customOpacity')
  @Watch('legibilitySize')
  updateStyles() {
    // Aplica defaults da variante antes dos overrides manuais
    this.applyVariantDefaults()
    if (this.zIndex !== null && this.zIndex !== undefined) {
      this.el.style.zIndex = `${this.zIndex}`
      this.el.style.setProperty('--z-index-layer-4', `${this.zIndex}`)
    }
    // bgColor e customOpacity sobrepõem os valores da variante
    if (this.bgColor !== null) {
      this.el.style.setProperty('--surface-overlay-scrim', this.bgColor)
    }
    if (this.customOpacity !== null) {
      this.el.style.setProperty('--br-scrim-opacity', `${this.customOpacity}`)
    }
  }

  @Watch('variant')
  handleVariantChange() {
    this.updateStyles()
    if (this.isActive) {
      if (this.isLegibilityVariant) {
        this.removeGlobalEventInterception()
        this.resetScrolling()
        this.cleanupSpotlight()
        this.setupLegibility()
      } else if (this.variant === 'spotlight') {
        this.cleanupLegibility()
        this.addGlobalEventInterception()
        this.setupSpotlight()
      } else {
        this.cleanupLegibility()
        this.addGlobalEventInterception()
        this.cleanupSpotlight()
      }
    }
  }

  @Watch('spotlightTargetId')
  @Watch('spotlightPadding')
  @Watch('spotlightShape')
  handleSpotlightChange() {
    if (this.isActive) {
      this.setupSpotlight()
    }
  }

  @Watch('activator')
  handleActivatorChange() {
    this.removeActivatorClickListener()
    this.setupActivator()
  }

  componentWillLoad() {
    this.hasActivatorSlot = this.checkActivatorSlot('activator')
    this.displayModeExplicit = this.el.hasAttribute('display-mode')

    this.isActive = this.isLegibilityVariant ? true : this.isOpen
  }

  componentDidLoad() {
    this.updateStyles()
    this.setupActivator()
    this.setupActivatorSlotClick()
    this.addContentEventListener()
    this.listenToModalEvents()

    if (this.isLegibilityVariant) {
      this.setupLegibility()
    } else if (this.isActive) {
      this.applyScrollStrategy()
      this.addGlobalEventInterception()
      const modalChild = this.findModalChild()
      if (modalChild === null) {
        this.focusFirstElement()
        this.setupSpotlight()
      } else {
        this.suppressFocusGuard()
      }
    }
  }

  /**
   * Adiciona event listener ao contentRef para capturar eventos de teclado
   * e gerenciar a navegação por teclado dentro do scrim.
   */
  private addContentEventListener() {
    if (this.contentRef !== null) {
      this.contentRef.addEventListener('keydown', this.handleKeyDown)
    }
  }

  connectedCallback() {
    if (this.scrollStrategy === 'close') {
      const currentScroll = window.scrollY
      this.lastScrollTop = currentScroll
      this.scrollOrigin = currentScroll
      window.addEventListener('scroll', this.handleScrollClose)
    }
  }

  disconnectedCallback() {
    // Limpar timeouts pendentes
    if (this.focusTimeoutId !== null) {
      clearTimeout(this.focusTimeoutId)
      this.focusTimeoutId = null
    }
    if (this.restoreFocusTimeoutId !== null) {
      clearTimeout(this.restoreFocusTimeoutId)
      this.restoreFocusTimeoutId = null
    }

    window.removeEventListener('scroll', this.handleScrollClose)
    window.removeEventListener('scroll', this.handleBlockScroll)
    if (this.contentRef !== null) {
      this.contentRef.removeEventListener('keydown', this.handleKeyDown)
    }
    this.removeActivatorClickListener()
    this.cleanupActivatorSlotClick()
    this.removeGlobalEventInterception()
    this.removeModalEventListener()
    this.cleanupSpotlight()
    this.cleanupLegibility()
    if (this.modalFocusSuppressId !== null) {
      clearTimeout(this.modalFocusSuppressId)
      this.modalFocusSuppressId = null
    }
  }

  // ======================================================================
  // GERENCIAMENTO DE COMPONENTES FILHOS
  // ======================================================================

  /**
   * Ativa o guard que suprime `handleGlobalFocus` enquanto a modal filha executa
   * sua sequência assíncrona de foco (`moveFocusIntoModal`).
   *
   * O guard é desativado assim que o evento `brModalFocusReady` chega (via
   * `handleModalFocusReady`). O timeout é apenas um safety net para o caso de
   * o evento nunca chegar (ex: erro interno na modal).
   */
  private suppressFocusGuard(safetyMs: number = 1000) {
    this.isModalFocusing = true
    if (this.modalFocusSuppressId !== null) {
      clearTimeout(this.modalFocusSuppressId)
    }
    this.modalFocusSuppressId = setTimeout(() => {
      this.isModalFocusing = false
      this.modalFocusSuppressId = null
    }, safetyMs)
  }

  /**
   * Handler para o evento `brModalFocusReady` emitido pela modal filha.
   * Desativa o guard imediatamente assim que o foco está estabilizado,
   * cancelando o timeout de segurança do `suppressFocusGuard`.
   */
  private handleModalFocusReady = () => {
    if (!this.isModalFocusing) return
    this.isModalFocusing = false
    if (this.modalFocusSuppressId !== null) {
      clearTimeout(this.modalFocusSuppressId)
      this.modalFocusSuppressId = null
    }
  }

  /**
   * Handler para evento brModalOpen emitido por modal filha
   */
  private handleModalOpen = (event: Event) => {
    const modalElement = event.target as HTMLElement
    if (!this.isDirectChild(modalElement)) return

    this.suppressFocusGuard()

    if (!this.isOpen) {
      this.open()
    }
  }

  /**
   * Handler para evento brModalClose emitido por modal filha
   */
  private handleModalClose = (event: Event) => {
    const modalElement = event.target as HTMLElement
    if (!this.isDirectChild(modalElement)) return

    this.close()
  }

  /**
   * Verifica se um elemento é filho direto deste scrim (não nested)
   */
  private isDirectChild(element: HTMLElement): boolean {
    const children = this.getDirectChildren()
    return children.some((child) => child === element || child.contains(element))
  }

  /**
   * Adiciona listeners para eventos brModalOpen, brModalClose e brModalFocusReady das modais filhas
   */
  private listenToModalEvents() {
    this.el.addEventListener('brModalOpen', this.handleModalOpen)
    this.el.addEventListener('brModalClose', this.handleModalClose)
    this.el.addEventListener('brModalOpened', this.handleModalFocusReady)
  }

  /**
   * Remove listeners dos eventos da modal
   */
  private removeModalEventListener() {
    this.el.removeEventListener('brModalOpen', this.handleModalOpen)
    this.el.removeEventListener('brModalClose', this.handleModalClose)
    this.el.removeEventListener('brModalOpened', this.handleModalFocusReady)
  }

  /**
   * Retorna os elementos filhos diretos do scrim via slot padrão
   */
  private getDirectChildren(): Element[] {
    const shadowRoot = this.el.shadowRoot
    if (shadowRoot === null) return []

    const slot = shadowRoot.querySelector('slot:not([name])') as HTMLSlotElement | null
    if (slot === null) return []

    return slot.assignedElements()
  }

  /**
   * Busca uma modal filha direta do scrim
   */
  private findModalChild(): HTMLBrModalElement | null {
    const children = this.getDirectChildren()
    const modalElement = children.find((child) => child.tagName === 'BR-MODAL')
    return (modalElement as HTMLBrModalElement | undefined) ?? null
  }

  /**
   * Verifica se há uma modal filha com autoClose=false (padrão ou explícito)
   * Retorna true se:
   * - Existe modal filha E autoClose não é explicitamente true
   */
  private hasModalWithAutoClose(): boolean {
    const modal = this.findModalChild()
    return modal !== null && modal.autoClose !== true
  }

  /**
   * Fecha a modal filha direta, se existir e estiver aberta.
   * Chamado quando o scrim é fechado (ex: via ESC) para garantir sincronização de estado.
   */
  private closeModalChild() {
    const modal = this.findModalChild()
    if (modal !== null && modal.show) {
      modal.show = false
    }
  }

  // ======================================================================
  // GERENCIAMENTO DO ACTIVATOR (ELEMENTO ACIONADOR)
  // ======================================================================
  private checkActivatorSlot(slotName: string): boolean {
    return this.el.querySelector(`[slot="${slotName}"]`) !== null
  }

  private setupActivatorSlotClick() {
    if (this.el.shadowRoot === null) return

    const slotEl = this.el.shadowRoot.querySelector('slot[name="activator"]') as HTMLSlotElement
    if (slotEl === null || slotEl === undefined) return

    const assignedElements = slotEl.assignedElements?.() ?? []

    if (assignedElements.length === 1) {
      const el = assignedElements[0] as HTMLElement
      if (el && typeof el.addEventListener === 'function') {
        el.addEventListener('click', this.handleActivatorClick)
      }
    } else if (assignedElements.length > 1) {
      console.warn('br-scrim: Slot "activator" deve conter apenas um elemento para aplicar o onClick automaticamente.')
    }
  }

  private setupActivator() {
    this.removeActivatorClickListener()
    this.activatorElement = null

    if (this.hasActivatorSlot || this.activator == null || this.activator === '') {
      return
    }

    if (this.activator === 'parent') {
      const parentElement = this.el.parentElement
      if (parentElement !== null) {
        this.activatorElement = parentElement
      }
    } else {
      try {
        const element = document.querySelector(this.activator)
        if (element instanceof HTMLElement) {
          this.activatorElement = element
        }
      } catch {
        console.warn(`br-scrim: Seletor activator inválido: ${this.activator}`)
      }
    }

    this.addActivatorClickListener()
  }

  private handleActivatorClick = () => {
    this.toggle()
  }

  private addActivatorClickListener() {
    if (this.activatorElement === null || this.activatorElement === undefined) return
    this.activatorElement.addEventListener('click', this.handleActivatorClick)
  }

  private removeActivatorClickListener() {
    if (this.activatorElement === null || this.activatorElement === undefined) return
    this.activatorElement.removeEventListener('click', this.handleActivatorClick)
  }

  private cleanupActivatorSlotClick() {
    if (this.el.shadowRoot === null) return

    const slotEl = this.el.shadowRoot.querySelector('slot[name="activator"]') as HTMLSlotElement
    const assignedElements = slotEl?.assignedElements?.() ?? []

    if (assignedElements.length === 1) {
      const element = assignedElements[0]
      if (element && typeof element.removeEventListener === 'function') {
        element.removeEventListener('click', this.handleActivatorClick)
      }
    }
  }

  // ======================================================================
  // ESTRATÉGIAS DE ROLAGEM (SCROLL)
  // ======================================================================
  private applyScrollStrategy() {
    if (this.scrollStrategy === 'block') {
      this.enableScrollBlock()
    } else if (this.scrollStrategy === 'close') {
      this.enableScrollWatcher()
    }
  }

  private getScrollPosition(): number {
    const targetWindow = this.el?.ownerDocument?.defaultView || window
    return targetWindow?.scrollY ?? targetWindow?.pageYOffset ?? 0
  }

  private enableScrollBlock() {
    this.scrollOffset = window.scrollY
    document.body.style.setProperty('position', 'fixed')
    document.body.style.setProperty('top', `-${this.scrollOffset}px`)
    document.body.style.setProperty('left', '0')
    document.body.style.setProperty('right', '0')
    document.body.style.setProperty('width', '100%')
    document.documentElement.style.setProperty('overflow', 'hidden')

    window.addEventListener('scroll', this.handleBlockScroll)
  }

  private enableScrollWatcher() {
    const currentScroll = window.scrollY
    this.lastScrollTop = currentScroll
    this.scrollOrigin = currentScroll
    window.addEventListener('scroll', this.handleScrollClose)
  }

  private handleBlockScroll = (e: Event) => {
    if (this.scrollStrategy === 'block' && this.isActive) {
      window.scrollTo(0, this.scrollOffset)
      e.preventDefault()
      e.stopPropagation()
    }
  }

  private resetScrolling() {
    if (this.scrollStrategy === 'block') {
      document.body.style.removeProperty('position')
      document.body.style.removeProperty('top')
      document.body.style.removeProperty('left')
      document.body.style.removeProperty('right')
      document.body.style.removeProperty('width')
      document.documentElement.style.removeProperty('overflow')
      window.removeEventListener('scroll', this.handleBlockScroll)
      window.scrollTo(0, this.scrollOffset)
    }
    window.removeEventListener('scroll', this.handleScrollClose)
    this.scrollOrigin = null
  }

  private handleScrollClose = () => {
    if (this.scrollStrategy === 'close' && this.isActive) {
      const currentScroll = this.getScrollPosition()
      const reference = this.scrollOrigin ?? this.lastScrollTop
      if (Math.abs(currentScroll - reference) > this.scrollThreshold) {
        this.scrollOrigin = null
        // Não fecha se tiver modal filha com autoClose=false (padrão)
        if (this.hasModalWithAutoClose()) {
          return
        }
        this.isOpen = false
        return
      }
      this.lastScrollTop = currentScroll
    }
  }

  // ======================================================================
  // ACESSIBILIDADE E GERENCIAMENTO DE FOCO
  // ======================================================================
  private focusFirstElement() {
    if (this.contentRef == null) return

    const modalChild = this.findModalChild()

    if (modalChild !== null && modalChild.show) {
      // Devolver foco ao container da modal — a própria modal gerencia Tab/Shift+Tab internamente
      const modalDialogEl = modalChild.shadowRoot?.querySelector<HTMLElement>('.br-modal')
      modalDialogEl?.focus()
      return
    }

    this.contentRef.setAttribute('tabindex', '-1')
    this.contentRef.focus()

    const focusableElements = this.getFocusableElements()
    if (focusableElements.length > 0) {
      const firstElement = focusableElements[0]
      if (firstElement !== null && firstElement !== undefined) {
        firstElement.scrollIntoView({ block: 'nearest', inline: 'nearest' })
        firstElement.focus({ preventScroll: true })
      }
    }
  }

  private restoreFocus() {
    let targetElement: HTMLElement | null = null

    if (this.previouslyFocusedElement instanceof HTMLElement && this.previouslyFocusedElement.isConnected) {
      targetElement = this.previouslyFocusedElement
    } else if (this.activatorElement instanceof HTMLElement) {
      targetElement = this.activatorElement
    } else {
      const slotEl = this.el.shadowRoot?.querySelector('slot[name="activator"]') as HTMLSlotElement
      if (this.hasActivatorSlot) {
        const assignedElements = slotEl.assignedElements?.() ?? []
        if (assignedElements.length > 0 && assignedElements[0] instanceof HTMLElement) {
          targetElement = assignedElements[0] as HTMLElement
        }
      }
    }

    if (targetElement !== null && targetElement !== undefined) {
      this.restoreFocusTimeoutId = setTimeout(() => {
        try {
          targetElement?.focus()
        } catch (error) {
          console.warn('br-scrim: Erro ao restaurar foco:', error)
        }
      }, 100)
    }
  }

  private getFocusableElements(): HTMLElement[] {
    if (this.contentRef == null) return []

    const defaultSlot = this.contentRef.querySelector('slot:not([name])') as HTMLSlotElement
    if (defaultSlot === null || defaultSlot === undefined) return []

    const assignedElements = defaultSlot.assignedElements({ flatten: true })
    const focusableElements: HTMLElement[] = []

    assignedElements.forEach((el) => {
      if (!(el instanceof HTMLElement)) return

      if (el.matches(Scrim.FOCUSABLE_SELECTORS)) {
        focusableElements.push(el)
      }

      const nestedFocusables = Array.from(el.querySelectorAll<HTMLElement>(Scrim.FOCUSABLE_SELECTORS))
      focusableElements.push(...nestedFocusables)
    })

    return this.filterVisibleElements(focusableElements)
  }

  private filterVisibleElements(elements: HTMLElement[]): HTMLElement[] {
    return elements.filter(
      (el) => el.offsetWidth > 0 && el.offsetHeight > 0 && getComputedStyle(el).visibility !== 'hidden'
    )
  }

  // ======================================================================
  // NAVEGAÇÃO POR TECLADO E INTERCEPTAÇÃO DE EVENTOS
  // ======================================================================
  private handleKeyDown = (event: KeyboardEvent) => {
    if (!this.isActive) return

    if (this.shouldHandleEscape(event)) {
      this.isOpen = false
      return
    }

    if (event.key === 'Tab') {
      const modalChild = this.findModalChild()
      if (modalChild !== null && modalChild.show) return

      this.handleTabNavigation(event)
    }
  }

  /**
   * Intercepta todos os eventos de teclado globalmente quando o scrim está ativo.
   *
   * Usa `composedPath()[0]` para obter o elemento real com foco, mesmo dentro de shadow roots.
   * `event.target` é retargetado ao cruzar shadow boundaries e não reflete o elemento concreto
   * quando o foco está dentro do shadow DOM de componentes filhos como `br-modal`.
   */
  private handleGlobalKeyDown = (event: KeyboardEvent) => {
    if (!this.isActive) return

    const realTarget = (event.composedPath?.()[0] ?? event.target) as HTMLElement

    if (!this.isElementInsideScrim(realTarget)) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      this.focusFirstElement()
      return
    }

    if (event.key === 'Escape' && !this.disableCloseOnClick) {
      event.preventDefault()
      this.isOpen = false
    }
  }

  // Intercepta TODOS os eventos de clique globalmente quando scrim está ativo
  private handleGlobalClick = (event: MouseEvent) => {
    if (!this.isActive) return

    const target = (event.composedPath?.()[0] ?? event.target) as HTMLElement

    if (!this.isElementInsideScrim(target)) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      if (!this.disableCloseOnClick) {
        this.close()
      }
      return
    }
  }

  /**
   * Intercepta eventos de foco globalmente
   */
  private handleGlobalFocus = (event: FocusEvent) => {
    if (!this.isActive) return

    const realTarget = (event.composedPath?.()[0] ?? event.target) as HTMLElement
    const insideScrim = this.isElementInsideScrim(realTarget)

    if (this.isModalFocusing) return

    if (!insideScrim) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      requestAnimationFrame(() => {
        this.focusFirstElement()
      })
    }
  }

  private shouldHandleEscape(event: KeyboardEvent): boolean {
    return (event.key === 'Escape' || event.key === 'Esc') && this.isActive
  }

  private handleTabNavigation(event: KeyboardEvent): void {
    const focusableElements = this.getFocusableElements()
    if (focusableElements.length === 0) {
      event.preventDefault()
      return
    }

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    const isTabForward = !event.shiftKey

    if (this.shouldTrapFocus(isTabForward, firstElement, lastElement, event)) {
      event.preventDefault()
      this.wrapFocus(isTabForward, firstElement, lastElement)
    }
  }

  private shouldTrapFocus(
    isTabForward: boolean,
    firstElement: HTMLElement,
    lastElement: HTMLElement,
    event?: KeyboardEvent
  ): boolean {
    const activeEl = (event?.composedPath?.()[0] ?? document.activeElement) as HTMLElement
    return (isTabForward && activeEl === lastElement) || (!isTabForward && activeEl === firstElement)
  }

  private wrapFocus(isTabForward: boolean, firstElement: HTMLElement, lastElement: HTMLElement): void {
    ;(isTabForward ? firstElement : lastElement).focus()
  }

  /**
   * Verifica se um elemento está dentro do scrim.
   * Funciona com shadow DOM: aceita tanto o host (br-modal) quanto elementos
   * dentro de shadow roots filhos — verifica o host mais próximo via getRootNode.
   */
  private isElementInsideScrim(element: HTMLElement): boolean {
    if (this.el.contains(element)) {
      return true
    }

    let node: Node = element
    while (node !== null && node !== document) {
      const root = node.getRootNode()
      if (root instanceof ShadowRoot) {
        const host = root.host
        if (this.el.contains(host)) {
          return true
        }
        node = host
      } else {
        break
      }
    }

    return false
  }

  /**
   * Adiciona interceptação global de eventos
   */
  private addGlobalEventInterception() {
    document.addEventListener('keydown', this.handleGlobalKeyDown, true)

    // Intercepta eventos de clique globalmente
    document.addEventListener('click', this.handleGlobalClick, true)
    document.addEventListener('mousedown', this.handleGlobalClick, true)
    document.addEventListener('mouseup', this.handleGlobalClick, true)

    // Intercepta eventos de foco.
    document.addEventListener('focusin', this.handleGlobalFocus, true)
  }

  /**
   * Remove interceptação global de eventos
   */
  private removeGlobalEventInterception() {
    document.removeEventListener('keydown', this.handleGlobalKeyDown, true)

    document.removeEventListener('click', this.handleGlobalClick, true)
    document.removeEventListener('mousedown', this.handleGlobalClick, true)
    document.removeEventListener('mouseup', this.handleGlobalClick, true)

    document.removeEventListener('focusin', this.handleGlobalFocus, true)
  }

  /**
   * Handler para clique na máscara do scrim.
   * Fecha o scrim se disableCloseOnClick for false e não houver modal filha com autoClose=false.
   * Não afeta o comportamento de ESC, que sempre fecha (W3C Dialog Pattern).
   */
  private handleMaskClick = (_event: MouseEvent) => {
    if (!this.disableCloseOnClick) {
      if (this.hasModalWithAutoClose()) {
        return
      }
      this.close()
    }
  }

  // ======================================================================
  // VARIANTE SEMÂNTICA (FOCO / LEGIBILIDADE / INIBIÇÃO)
  // ======================================================================

  /**
   * Retorna true quando a variante é 'legibility'.
   * Nesse modo o scrim é puramente decorativo: nunca fecha, nunca intercepta
   * eventos de teclado/foco/clique e nunca bloqueia rolagem.
   */
  private get isLegibilityVariant(): boolean {
    return this.variant === 'legibility'
  }

  /**
   * Resolve o display-mode efetivo.
   * Para a variante 'legibility', usa 'parent' por padrão — a menos que o
   * atributo `display-mode` tenha sido explicitamente definido pelo usuário.
   */
  private get effectiveDisplayMode(): 'fullscreen' | 'parent' {
    if (this.variant === 'legibility' && !this.displayModeExplicit) {
      return 'parent'
    }

    return this.displayMode
  }

  /**
   * Aplica as especificações de cor e opacidade conforme a variante selecionada.
   */
  private applyVariantDefaults() {
    this.el.style.removeProperty('--surface-overlay-scrim')
    this.el.style.removeProperty('--br-scrim-opacity')
    this.el.style.removeProperty('--br-scrim-mask-size')

    switch (this.variant) {
      case 'focus':
      case 'spotlight':
        if (this.bgColor === null) {
          this.el.style.setProperty('--surface-overlay-scrim', '#000000')
        }
        if (this.customOpacity === null) {
          this.el.style.setProperty('--br-scrim-opacity', '0.4')
        }
        break
      case 'legibility': {
        if (this.legibilitySize !== null) {
          this.el.style.setProperty('--br-scrim-mask-size', this.legibilitySize)
        } else {
          this.el.style.removeProperty('--br-scrim-mask-size')
        }

        if (this.bgColor === null) {
          this.el.style.setProperty('--surface-overlay-scrim', '#000000')
        }

        if (this.customOpacity === null) {
          // Se bgColor for um gradiente, o usuário deve setar customOpacity=1 ou omitir
          this.el.style.setProperty('--br-scrim-opacity', '0.64')
        }
        break
      }
    }
  }

  // ======================================================================
  // SCRIM VAZADO (SPOTLIGHT)
  // ======================================================================

  /**
   * Inicializa o modo de scrim vazado: resolve o elemento alvo, calcula o rect
   * inicial e instala um ResizeObserver para atualizar automaticamente.
   * Só opera quando `variant === 'spotlight'`.
   */
  private setupSpotlight() {
    this.cleanupSpotlight()

    if (this.variant !== 'spotlight') return

    const target = this.resolveSpotlightTarget()
    if (target === null) return

    this.spotlightTargetElement = target
    this.computeSpotlightRect()

    if (typeof ResizeObserver !== 'undefined') {
      this.spotlightResizeObserver = new ResizeObserver(this.computeSpotlightRect)
      this.spotlightResizeObserver.observe(target)
    }

    window.addEventListener('resize', this.computeSpotlightRect)
    window.addEventListener('scroll', this.computeSpotlightRect, true)
  }

  /**
   * Desmonta o modo de scrim vazado, removendo observers e limpando o estado.
   */
  private cleanupSpotlight() {
    if (this.spotlightResizeObserver !== null) {
      this.spotlightResizeObserver.disconnect()
      this.spotlightResizeObserver = null
    }
    this.spotlightTargetElement = null
    this.spotlightRect = null
    window.removeEventListener('resize', this.computeSpotlightRect)
    window.removeEventListener('scroll', this.computeSpotlightRect, true)
  }

  /**
   * Resolve o elemento alvo do spotlight via prop `spotlightTargetId` (seletor CSS).
   */
  private resolveSpotlightTarget(): Element | null {
    if (this.spotlightTargetId !== null && this.spotlightTargetId !== '') {
      try {
        return document.querySelector(this.spotlightTargetId)
      } catch {
        console.warn(`br-scrim: Seletor spotlightTargetId inválido: ${this.spotlightTargetId}`)
      }
    }

    return null
  }

  /**
   * Recalcula as coordenadas da fresta com base no bounding rect do elemento alvo.
   * Coordenadas são relativas ao viewport (mode-fullscreen) ou ao elemento pai (mode-parent).
   */
  private computeSpotlightRect = () => {
    if (this.spotlightTargetElement === null) return

    const targetRect = this.spotlightTargetElement.getBoundingClientRect()

    if (this.effectiveDisplayMode === 'parent' && this.el.parentElement !== null) {
      const parentRect = this.el.parentElement.getBoundingClientRect()
      this.spotlightRect = new DOMRect(
        targetRect.left - parentRect.left,
        targetRect.top - parentRect.top,
        targetRect.width,
        targetRect.height
      )
    } else {
      this.spotlightRect = new DOMRect(targetRect.left, targetRect.top, targetRect.width, targetRect.height)
    }
  }

  // ======================================================================
  // VARIANTE LEGIBILITY — POSICIONAMENTO POR BOUNDING RECT
  // ======================================================================

  /**
   * Inicializa o posicionamento da variante legibility.
   *
   * Aplica `position: fixed` com as coordenadas exatas do elemento pai via
   * `getBoundingClientRect`, sem exigir nenhum CSS adicional no pai.
   *
   * Com `ResizeObserver`, observa o pai **e todos os seus ancestrais**: quando um
   * elemento irmão muda de tamanho (ex: menu abrindo), o ancestral comum redimensiona
   * e o callback reposiciona o scrim automaticamente.
   */
  private setupLegibility() {
    this.cleanupLegibility()

    if (!this.isLegibilityVariant) return

    const parent = this.el.parentElement
    if (parent === null) return

    if (typeof ResizeObserver !== 'undefined') {
      this.legibilityResizeObserver = new ResizeObserver(this.computeLegibilityRect)
      this.legibilityResizeObserver.observe(parent)

      // Observa ancestrais até document.body para detectar deslocamentos causados
      // por elementos irmãos que empurram o pai (ex: menu abrindo acima dele).
      let ancestor: Element | null = parent.parentElement
      while (ancestor !== null && ancestor !== document.documentElement) {
        this.legibilityResizeObserver.observe(ancestor)
        ancestor = ancestor.parentElement
      }
    } else {
      requestAnimationFrame(() => requestAnimationFrame(this.computeLegibilityRect))
      window.addEventListener('resize', this.computeLegibilityRect)
    }

    window.addEventListener('scroll', this.computeLegibilityRect, true)
    window.addEventListener('load', this.computeLegibilityRect)
  }

  /**
   * Desmonta o posicionamento da variante legibility, removendo observers e listeners
   * e limpando os estilos inline aplicados ao div interno do scrim.
   */
  private cleanupLegibility() {
    if (this.legibilityResizeObserver !== null) {
      this.legibilityResizeObserver.disconnect()
      this.legibilityResizeObserver = null
    }
    window.removeEventListener('resize', this.computeLegibilityRect)
    window.removeEventListener('scroll', this.computeLegibilityRect, true)
    window.removeEventListener('load', this.computeLegibilityRect)
    this.resetLegibilityStyles()
  }

  /**
   * Recalcula as coordenadas do elemento pai via getBoundingClientRect e aplica
   * como estilos inline no div interno, sobrepondo o `inset: 0` do mode-fullscreen.
   */
  private computeLegibilityRect = () => {
    const parent = this.el.parentElement
    if (parent === null || this.scrimDivRef === null) return

    const rect = parent.getBoundingClientRect()

    this.scrimDivRef.style.setProperty('position', 'fixed')
    this.scrimDivRef.style.setProperty('inset', 'unset')
    this.scrimDivRef.style.setProperty('top', `${rect.top}px`)
    this.scrimDivRef.style.setProperty('left', `${rect.left}px`)
    this.scrimDivRef.style.setProperty('width', `${rect.width}px`)
    this.scrimDivRef.style.setProperty('height', `${rect.height}px`)
  }

  /**
   * Remove os estilos inline de posicionamento aplicados pelo computeLegibilityRect.
   */
  private resetLegibilityStyles() {
    if (this.scrimDivRef === null) return
    this.scrimDivRef.style.removeProperty('position')
    this.scrimDivRef.style.removeProperty('inset')
    this.scrimDivRef.style.removeProperty('top')
    this.scrimDivRef.style.removeProperty('left')
    this.scrimDivRef.style.removeProperty('width')
    this.scrimDivRef.style.removeProperty('height')
  }

  // ======================================================================
  // UTILITÁRIOS
  // ======================================================================

  /**
   * Renderiza o SVG de overlay do scrim vazado (spotlight).
   * Retorna null quando não há elemento alvo resolvido.
   */
  private renderSpotlightOverlay(maskId: string, rect: DOMRect, padding: number) {
    const spotlightShape =
      this.spotlightShape === 'circle' ? (
        <ellipse
          cx={rect.left + rect.width / 2}
          cy={rect.top + rect.height / 2}
          rx={rect.width / 2 + padding}
          ry={rect.height / 2 + padding}
          fill="black"
        />
      ) : (
        <rect
          x={rect.left - padding}
          y={rect.top - padding}
          width={rect.width + padding * 2}
          height={rect.height + padding * 2}
          rx={this.spotlightShape === 'rounded' ? 8 : 0}
          ry={this.spotlightShape === 'rounded' ? 8 : 0}
          fill="black"
        />
      )

    return (
      <svg
        class="spotlight-overlay"
        aria-hidden="true"
        onClick={this.handleMaskClick}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="white" />
            {spotlightShape}
          </mask>
        </defs>
        <rect class="spotlight-overlay-rect" width="100%" height="100%" mask={`url(#${maskId})`} />
      </svg>
    )
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-scrim': true,
      opened: this.isActive,
      'scroll-block': this.scrollStrategy === 'block',
      'position-top': this.positionContent === 'top',
      'position-bottom': this.positionContent === 'bottom',
      'position-left': this.positionContent === 'left',
      'position-right': this.positionContent === 'right',
      'position-center': this.positionContent === 'center',
      'mode-fullscreen': this.effectiveDisplayMode === 'fullscreen',
      'mode-parent': this.effectiveDisplayMode === 'parent',
      'variant-focus': this.variant === 'focus',
      'variant-spotlight': this.variant === 'spotlight',
      'variant-legibility': this.variant === 'legibility',
      'legibility-anchor-top': this.variant === 'legibility' && this.legibilityAnchor === 'top',
      'legibility-anchor-bottom': this.variant === 'legibility' && this.legibilityAnchor === 'bottom',
      'legibility-anchor-left': this.variant === 'legibility' && this.legibilityAnchor === 'left',
      'legibility-anchor-right': this.variant === 'legibility' && this.legibilityAnchor === 'right',
      'legibility-anchor-center': this.variant === 'legibility' && this.legibilityAnchor === 'center',
      'has-spotlight': this.spotlightRect !== null,
    }
  }

  // ======================================================================
  // MÉTODOS PÚBLICOS (API)
  // ======================================================================
  /**
   * Define o limite de rolagem para o fechamento automático do scrim.
   */
  @Method()
  async setScrollThreshold(threshold: number) {
    this.scrollThreshold = threshold
  }

  /**
   * Método público para exibir o scrim
   */
  @Method()
  async open() {
    this.isOpen = true
  }

  /**
   * Método público para esconder o scrim
   */
  @Method()
  async close() {
    if (this.isLegibilityVariant) return
    this.isOpen = false
  }

  /**
   * Método público para alternar o estado de exibição do scrim
   */
  @Method()
  async toggle() {
    if (this.isLegibilityVariant) return
    this.isOpen = !this.isActive
  }

  /**
   * Recalcula manualmente a posição e dimensões da fresta do scrim vazado.
   * Útil quando o elemento alvo muda de posição sem disparar resize ou scroll.
   */
  @Method()
  async updateSpotlight() {
    this.computeSpotlightRect()
  }

  render() {
    const maskId = `${this.customId}-spotlight-mask`
    const padding = this.spotlightPadding
    const rect = this.spotlightRect

    return (
      <Host>
        <slot name="activator"></slot>
        <div
          ref={(el) => (this.scrimDivRef = el as HTMLDivElement | null)}
          class={this.getCssClassMap()}
          id={this.customId}
          role={this.isLegibilityVariant ? 'presentation' : 'dialog'}
          aria-modal={this.isLegibilityVariant ? undefined : 'true'}
          aria-label={this.isLegibilityVariant ? undefined : this.ariaLabel || 'Conteúdo do diálogo'}
          data-visible={this.isActive ? 'true' : 'false'}
        >
          <div class="mask" onClick={this.isLegibilityVariant ? undefined : this.handleMaskClick}></div>
          {rect !== null && this.renderSpotlightOverlay(maskId, rect, padding)}
          <div
            ref={(el) => (this.contentRef = el)}
            id={`${this.customId}-content`}
            class="content"
            tabIndex={-1}
            onKeyDown={this.handleKeyDown}
          >
            <slot></slot>
          </div>
        </div>
      </Host>
    )
  }
}
